import React from 'react';
import { Link } from 'react-router-dom';
import useMeta from '../hooks/useMeta';

export default function Diamantboring() {
  useMeta(
    'Diamantboring - Danry Diamant Boring ApS',
    'Professionel diamantboring til ventilation, installationer og specialopgaver udført med høj præcision og moderne udstyr.'
  );

  const processSteps = [
    {
      number: '01',
      title: 'Dialog',
      description: 'Vi forventningsafstemmer og rådgiver om den mest optimale løsning til dit projekt.',
      icon: 'fa-solid fa-comments',
    },
    {
      number: '02',
      title: 'Opmåling',
      description: 'Vi foretager præcis opmåling på stedet for at sikre millimeterpræcise boringer.',
      icon: 'fa-solid fa-ruler-combined',
    },
    {
      number: '03',
      title: 'Udførelse',
      description: 'Vi udfører boringen med professionelt udstyr, hurtigt, sikkert og med minimalt støv.',
      icon: 'fa-solid fa-screwdriver-wrench',
    },
    {
      number: '04',
      title: 'Aflevering',
      description: 'Vi rydder pænt op efter os og afleverer det færdige resultat til din fulde tilfredshed.',
      icon: 'fa-solid fa-circle-check',
    },
  ];

  // Cloudflare resized image URLs
  const ventilationImg = 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/0b4cb68f-6aab-477c-8129-a4a720b56096/118428733-696760547579131-6862768956582941410-n.jpg';
  const installationImg = 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/0b4cb68f-6aab-477c-8129-a4a720b56096/95410379-622094791712374-906619273508552704-n.jpg';
  const specialImg = 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/0b4cb68f-6aab-477c-8129-a4a720b56096/95373473-622094861712367-3282176246250733568-n.jpg';

  return (
    <div className="pt-[90px] lg:pt-[120px] bg-white">
      {/* Hero Section */}
      <section className="relative bg-neutral-50 py-28 border-b border-neutral-200 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center max-w-4xl mx-auto flex flex-col gap-6">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-neutral-900 tracking-tight leading-tight uppercase">
            Professionel diamantboring til alle behov
          </h1>
          <p className="text-lg sm:text-xl text-neutral-700 leading-relaxed">
            Hos Danry Diamant Boring ApS leverer vi højteknologisk diamantboring udført med absolut præcision. Vi borer i alle typer hårde materialer – herunder armeret beton, murværk, granit og asfalt. Vi servicerer både private boligejere, erhvervsvirksomheder og entreprenører med skræddersyede løsninger.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <Link
              to="/kontakt"
              className="px-8 py-4 rounded-full bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-base transition-colors shadow-md shadow-brand-orange/10"
            >
              Få et uforpligtende tilbud
            </Link>
            <a
              href="tel:+4522971411"
              className="flex items-center gap-3 px-8 py-4 rounded-full bg-transparent hover:bg-neutral-50 border border-neutral-950 text-neutral-800 font-bold text-base transition-colors"
            >
              <i className="fa-solid fa-phone text-brand-orange" />
              22 97 14 11
            </a>
          </div>
        </div>
      </section>

      {/* Services Sections */}
      <section className="py-20 bg-white flex flex-col gap-24">
        {/* Section 1: Ventilation */}
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 flex flex-col gap-6">
            <div className="text-brand-orange text-3xl">
              <i className="fa-solid fa-wind" />
            </div>
            <h2 className="text-3xl font-bold text-neutral-900 tracking-tight uppercase">
              Diamantboring til ventilation
            </h2>
            <p className="text-neutral-700 leading-relaxed">
              Korrekt ventilation er afgørende for et sundt indeklima i både boliger og erhvervsbygninger. Vi udfører præcise ventilationsboringer til emhætter, badeværelsesventilatorer, friskluftsventiler og større ventilationsanlæg (Genvex).
            </p>
            <p className="text-neutral-600 leading-relaxed">
              Vi borer huller i alle gængse dimensioner og sikrer, at boringen sker vinkelret eller i den præcise hældning, der kræves for optimal luftgennemstrømning og kondensafledning. Vores avancerede udstyr minimerer vibrationer og støv under arbejdet.
            </p>
            <ul className="flex flex-col gap-2.5 text-sm text-neutral-700">
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-check text-brand-orange" />
                Huller til emhætter og badeværelsesudsugning
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-check text-brand-orange" />
                Gennemføringer til store erhvervsventilationsanlæg
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-check text-brand-orange" />
                Boring i både murværk og tykke betonkonstruktioner
              </li>
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="relative rounded-none overflow-hidden border border-neutral-200 bg-neutral-50 aspect-4/3 shadow-lg">
              <img
                src={ventilationImg}
                alt="Diamantboring til ventilation udført i murværk"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Section 2: Installationer */}
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 lg:order-2 flex flex-col gap-6">
            <div className="text-brand-orange text-3xl">
              <i className="fa-solid fa-plug" />
            </div>
            <h2 className="text-3xl font-bold text-neutral-900 tracking-tight uppercase">
              Diamantboring til installationer
            </h2>
            <p className="text-neutral-700 leading-relaxed">
              Når der skal trækkes nye rør, kabler eller tekniske installationer, er diamantboring den mest skånsomme og præcise metode. Vi borer huller til VVS-installationer, fjernvarme, kloakrør og el-kabler gennem vægge, dæk og fundamenter.
            </p>
            <p className="text-neutral-600 leading-relaxed">
              Ved at benytte diamantboring undgår man de revner og svækkelser i betonkonstruktionen, som traditionelle opbrydningsmetoder kan medføre. Vi borer præcist der, hvor installationen skal placeres, hvilket sparer tid på efterfølgende tætnings- og reparationsarbejde.
            </p>
            <ul className="flex flex-col gap-2.5 text-sm text-neutral-700">
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-check text-brand-orange" />
                Rørgennemføringer til VVS, varme og sanitet
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-check text-brand-orange" />
                Kabelgennemføringer til el og fiber
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-check text-brand-orange" />
                Boring gennem fundamenter og etagedæk
              </li>
            </ul>
          </div>
          <div className="lg:col-span-6 lg:order-1">
            <div className="relative rounded-none overflow-hidden border border-neutral-200 bg-neutral-50 aspect-4/3 shadow-lg">
              <img
                src={installationImg}
                alt="Installation af ventilationssystem og rørgennemføringer"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Section 3: Specialboringer */}
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 flex flex-col gap-6">
            <div className="text-brand-orange text-3xl">
              <i className="fa-solid fa-screwdriver-wrench" />
            </div>
            <h2 className="text-3xl font-bold text-neutral-900 tracking-tight uppercase">
              Specialboringer efter behov
            </h2>
            <p className="text-neutral-700 leading-relaxed">
              Nogle projekter kræver utraditionelle løsninger. Vi udfører specialboringer tilpasset dine helt specifikke projektbehov. Det kan være boring i skæve vinkler, dybdeboringer gennem ekstremt tykke mure, eller boring under vanskelige pladsforhold.
            </p>
            <p className="text-neutral-600 leading-relaxed">
              Vi råder over fleksibelt grej, der kan monteres på stilladser, i snævre skakte eller fastgøres på lofter. Vi tænker i løsninger og rådgiver gerne arkitekter, ingeniører og entreprenører tidligt i processen for at sikre en sikker og økonomisk udførelse.
            </p>
            <ul className="flex flex-col gap-2.5 text-sm text-neutral-700">
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-check text-brand-orange" />
                Boring i vinkler op til 45 grader
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-check text-brand-orange" />
                Dybdeboring og boring med store diametre
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-check text-brand-orange" />
                Skræddersyede løsninger til komplekse byggeprojekter
              </li>
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="relative rounded-none overflow-hidden border border-neutral-200 bg-neutral-50 aspect-4/3 shadow-lg">
              <img
                src={specialImg}
                alt="Specialboring i tyk betonvæg"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Work Process Section */}
      <section className="py-20 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-12">
          <div className="text-center max-w-3xl mx-auto flex flex-col gap-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight uppercase">
              Hvordan vi arbejder – trin for trin
            </h2>
            <p className="text-neutral-600 leading-relaxed">
              Vi har en struktureret og gennemskuelig arbejdsproces, der sikrer, at dit projekt bliver leveret til tiden, i højeste kvalitet og uden ubehagelige overraskelser.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {/* Connecting line for desktop */}
            <div className="hidden lg:block absolute top-1/2 left-4 right-4 h-0.5 bg-neutral-200 -translate-y-12 z-0" />

            {processSteps.map((step, index) => (
              <div
                key={index}
                className="relative z-10 p-6 rounded-xl border border-neutral-200 bg-white flex flex-col gap-4 group hover:border-brand-orange/30 transition-all duration-300 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="text-brand-orange text-2xl group-hover:scale-110 transition-transform duration-300">
                    <i className={step.icon} />
                  </div>
                  <span className="text-3xl font-extrabold text-neutral-200 group-hover:text-brand-orange/20 transition-colors">
                    {step.number}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-bold text-neutral-900 uppercase">{step.title}</h3>
                  <p className="text-xs text-neutral-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-orange text-center relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-white/5 rounded-full blur-[100px] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 flex flex-col gap-8 relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight uppercase">
            Klar til at starte dit projekt?
          </h2>
          <p className="text-lg text-white/90 max-w-2xl mx-auto leading-relaxed">
            Uanset om du har brug for en enkelt boring eller et større tilbud, står vi klar til at hjælpe dig videre. Kontakt os i dag for et uforpligtende tilbud.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-2">
            <Link
              to="/kontakt"
              className="px-8 py-4 rounded-full bg-white hover:bg-neutral-50 text-brand-orange font-bold text-base transition-all duration-200 shadow-lg shadow-black/5"
            >
              Få et tilbud nu
            </Link>
            <a
              href="tel:+4522971411"
              className="px-8 py-4 rounded-full bg-transparent hover:bg-white/10 border border-white text-white font-bold text-base transition-all duration-200 flex items-center gap-3"
            >
              <i className="fa-solid fa-phone" />
              Ring 22 97 14 11
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
